/**
 * Display formatting. Ported from the prototype's helpers in
 * `docs/html/assets/js/app.js` so React screens render identically to the
 * design reference.
 */

const SYMBOLS = { USD: '$', EUR: '€', GBP: '£', JPY: '¥', INR: '₹' };

/** The symbol for a currency code, falling back to the code itself. */
export const currencySymbol = (code) => SYMBOLS[code] ?? (code ? `${code} ` : '$');

/**
 * A full money amount: `$185,000`.
 * Rounded to whole units — CRM figures are quoted in whole currency, and cents
 * on a $185,000 deal are noise that makes columns harder to scan.
 */
export function money(amount, code = 'USD') {
  if (amount == null) return '—';
  return currencySymbol(code) + Math.round(Number(amount)).toLocaleString('en-US');
}

/**
 * A compact money amount for tiles and axes: `$1.8M`, `$185K`.
 *
 * Trailing zeros are trimmed so `$2.00M` reads as `$2M`. That trimming used to
 * be `.replace(/\.?0+$/, '')`, whose `\.?` makes the decimal point optional —
 * so on a value with no decimal point at all it ate the integer's own trailing
 * zeros: `$10M` rendered as `$1M`, and `$1,000M` as `$1M` too. The executive
 * dashboard's y-axis ran `$6M · $8M · $1M`, which reads as a chart that falls
 * off a cliff. `Number()` drops only genuine trailing decimal zeros, and cannot
 * touch the integer part.
 */
export function moneyShort(amount, code = 'USD') {
  if (amount == null) return '—';
  const n = Number(amount);
  const symbol = currencySymbol(code);
  if (Math.abs(n) >= 1e6) return `${symbol}${Number((n / 1e6).toFixed(2))}M`;
  if (Math.abs(n) >= 1e3) return `${symbol}${Number((n / 1e3).toFixed(1))}K`;
  return symbol + n;
}

/** `1,284`. */
export const number = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US'));

/** `68%`, with the sign kept for deltas. */
export const percent = (n, withSign = false) =>
  n == null ? '—' : `${withSign && n > 0 ? '+' : ''}${Math.round(n)}%`;

/** Up to two initials, for avatars. */
export const initials = (name) =>
  String(name ?? '?')
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

/**
 * A stable avatar tint for a name.
 *
 * Hashed rather than random so the same person is the same colour on every
 * screen and across reloads — a colour that changes on refresh is worse than no
 * colour, because it stops being recognisable.
 */
export const avatarTone = (seed) =>
  'av-' + ((String(seed ?? '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % 6) + 1);

/** `5 Sep 2026`. */
export function date(value) {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** `14:30`. */
export function time(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

/** `5 Sep 2026, 14:30`. */
export function dateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/**
 * `just now`, `4 minutes ago`, `Yesterday`, then an absolute date.
 *
 * Switches to absolute past a week because "37 days ago" is harder to place than
 * "29 Jul" — relative time helps only while the reader can still hold the offset
 * in their head.
 */
export function relativeTime(value) {
  if (!value) return '—';
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return String(value);

  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 0) return date(value);
  if (seconds < 45) return 'just now';
  if (seconds < 3600) return `${Math.round(seconds / 60)} min ago`;
  if (seconds < 86_400) return `${Math.round(seconds / 3600)} h ago`;
  if (seconds < 172_800) return 'Yesterday';
  if (seconds < 604_800) return `${Math.round(seconds / 86_400)} days ago`;
  return date(value);
}

/** `Today`, `Tomorrow`, `Overdue 2d`, or a date — for due dates and follow-ups. */
export function dueLabel(value) {
  if (!value) return '—';
  const due = new Date(value);
  if (Number.isNaN(due.getTime())) return String(value);

  const startOfDay = (d) => Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const days = Math.round((startOfDay(due) - startOfDay(new Date())) / 86_400_000);

  if (days < 0) return `Overdue ${Math.abs(days)}d`;
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days < 7) return `In ${days} days`;
  return date(value);
}

/** Truncate for a table cell, on a word boundary where possible. */
export function truncate(text, max = 60) {
  const s = String(text ?? '');
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return (lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut) + '…';
}

/** File size, binary units — matches how the backend reports `max_upload_mb`. */
export function fileSize(bytes) {
  if (bytes == null) return '—';
  const n = Number(bytes);
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 ** 2).toFixed(1)} MB`;
}
